import { useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Award, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hotel-feedback-wins/artifacts/dl3pi3zz_flamingo_inn_logo_01.png";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

const printContent = (ref, title) => {
  const win = window.open("", "_blank");
  win.document.write(`<html><head><title>${title}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Source+Sans+3:wght@300;400;500;600&display=swap');
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Source Sans 3',sans-serif;display:flex;justify-content:center;align-items:center;min-height:100vh;padding:24px;background:#eee}
      @media print{body{background:#fff;padding:0}.no-print{display:none!important}}
    </style></head><body>${ref.current.innerHTML}
    <div class="no-print" style="text-align:center;margin-top:20px">
      <button onclick="window.print()" style="padding:12px 36px;background:#1a1a2e;color:#fff;border:none;cursor:pointer;font-size:13px;letter-spacing:2px;text-transform:uppercase;font-family:'Source Sans 3',sans-serif">Print</button>
    </div></body></html>`);
  win.document.close();
};

/* ============================================================
   TROPHY CERTIFICATE — elegant, formal, dark navy + gold
   ============================================================ */
export const TrophyCertificate = ({ winner, settings = {} }) => {
  const ref = useRef();
  const h = settings.hotel_name || "Flamingo Inn";
  const addr = settings.address || "2011 South Atlantic Avenue, Daytona Beach, FL";
  const ph = settings.phone || "386-451-5577";
  const em = settings.email || "daytonaflamingoinn@yahoo.com";
  const logo = settings.logo_url || LOGO_URL;
  const qr = winner.barcode || "N/A";

  return (
    <div>
      <div ref={ref}>
        <div style={{ maxWidth: 720, margin: "0 auto", fontFamily: "'Source Sans 3', sans-serif" }}>
          {/* Outer border */}
          <div style={{ border: "3px solid #1a1a2e", padding: 4, background: "#fff" }}>
            <div style={{ border: "1px solid #c9b037", padding: 0 }}>
              {/* Navy header */}
              <div style={{ background: "#1a1a2e", padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <img src={logo} alt="" style={{ height: 48, width: 48, objectFit: "contain", borderRadius: 4 }} />
                  <div>
                    <p style={{ color: "#c9b037", fontSize: 18, fontWeight: 700, fontFamily: "'Playfair Display', serif", margin: 0, letterSpacing: 1 }}>{h}</p>
                    <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 9, margin: 0, letterSpacing: 1 }}>{addr}</p>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ color: "#c9b037", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: 0 }}>Certificate No.</p>
                  <p style={{ color: "#fff", fontSize: 13, fontFamily: "monospace", fontWeight: 600, margin: 0 }}>{winner.barcode || "—"}</p>
                </div>
              </div>

              {/* Gold accent line */}
              <div style={{ height: 3, background: "linear-gradient(90deg, #c9b037, #e8d48b, #c9b037)" }} />

              {/* Body */}
              <div style={{ padding: "24px 28px" }}>
                <p style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 3, margin: "0 0 4px" }}>Complimentary Stay Certificate</p>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600, color: "#1a1a2e", margin: "0 0 16px", letterSpacing: 0.5 }}>Trophy Winner Award</h1>

                <div style={{ display: "flex", gap: 20 }}>
                  {/* Left details */}
                  <div style={{ flex: 1 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1, width: 80 }}>Awarded to</td>
                          <td style={{ padding: "6px 0", fontSize: 14, fontWeight: 600, color: "#1a1a2e", borderBottom: "1px solid #ddd" }}>{winner.guest_name}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Email</td>
                          <td style={{ padding: "6px 0", fontSize: 12, color: "#1a1a2e", borderBottom: "1px solid #ddd" }}>{winner.guest_email}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Prize</td>
                          <td style={{ padding: "6px 0", fontSize: 14, fontWeight: 600, color: "#c9b037", borderBottom: "1px solid #ddd" }}>{winner.prize}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>From</td>
                          <td style={{ padding: "6px 0", fontSize: 12, color: "#1a1a2e", borderBottom: "1px solid #ddd" }}>{h}</td>
                        </tr>
                        {winner.check_in_date && (
                          <tr>
                            <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>From date</td>
                            <td style={{ padding: "6px 0", fontSize: 12, fontWeight: 500, color: "#1a1a2e", borderBottom: "1px solid #ddd" }}>{fmtDate(winner.check_in_date)}</td>
                          </tr>
                        )}
                        {winner.check_out_date && (
                          <tr>
                            <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>To date</td>
                            <td style={{ padding: "6px 0", fontSize: 12, fontWeight: 500, color: "#1a1a2e", borderBottom: "1px solid #ddd" }}>{fmtDate(winner.check_out_date)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Right QR */}
                  <div style={{ width: 120, textAlign: "center", borderLeft: "1px dashed #ddd", paddingLeft: 20, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <QRCodeSVG value={qr} size={90} level="M" fgColor="#1a1a2e" />
                    <p style={{ fontSize: 8, color: "#888", marginTop: 6 }}>Scan to verify</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ background: "#f8f7f4", borderTop: "1px solid #eee", padding: "10px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p style={{ fontSize: 8, color: "#999", lineHeight: 1.5, maxWidth: 420 }}>
                  Present this certificate at the front desk upon check-in. Must be validated between the dates given. Non-transferable. Cannot be combined with other offers.
                </p>
                <p style={{ fontSize: 8, color: "#999" }}>{ph} | {em}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", marginTop: 16 }}>
        <Button onClick={() => printContent(ref, `Certificate ${winner.barcode}`)} className="bg-[#1a1a2e] text-white hover:bg-[#2a2a4e] px-8 py-3 text-xs uppercase tracking-widest" data-testid={`print-cert-${winner.id}`}>
          <Printer size={14} className="mr-2" /> Print Certificate
        </Button>
      </div>
    </div>
  );
};

/* ============================================================
   GIFT DRAW VOUCHER — warm, gold ticket, lighter feel
   ============================================================ */
export const GiftVoucher = ({ winner, settings = {} }) => {
  const ref = useRef();
  const h = settings.hotel_name || "Flamingo Inn";
  const addr = settings.address || "2011 South Atlantic Avenue, Daytona Beach, FL";
  const ph = settings.phone || "386-451-5577";
  const em = settings.email || "daytonaflamingoinn@yahoo.com";
  const logo = settings.logo_url || LOGO_URL;
  const qr = winner.barcode || winner.id || "N/A";

  return (
    <div>
      <div ref={ref}>
        <div style={{ maxWidth: 720, margin: "0 auto", fontFamily: "'Source Sans 3', sans-serif" }}>
          <div style={{ border: "3px solid #c9b037", padding: 4, background: "#fff" }}>
            <div style={{ border: "1px solid #e8d48b" }}>
              {/* Gold header */}
              <div style={{ background: "linear-gradient(135deg, #c9b037, #e8d48b)", padding: "12px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <img src={logo} alt="" style={{ height: 44, width: 44, objectFit: "contain", borderRadius: 4 }} />
                  <div>
                    <p style={{ color: "#1a1a2e", fontSize: 16, fontWeight: 700, fontFamily: "'Playfair Display', serif", margin: 0, letterSpacing: 1 }}>{h}</p>
                    <p style={{ color: "rgba(26,26,46,0.6)", fontSize: 9, margin: 0 }}>{addr}</p>
                  </div>
                </div>
                <p style={{ color: "#1a1a2e", fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase" }}>Gift Voucher</p>
              </div>

              {/* Body */}
              <div style={{ padding: "20px 28px" }}>
                <p style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 3, margin: "0 0 4px" }}>Congratulations</p>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600, color: "#1a1a2e", margin: "0 0 14px" }}>Gift Certificate</h1>

                <div style={{ display: "flex", gap: 20 }}>
                  <div style={{ flex: 1 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1, width: 70 }}>To</td>
                          <td style={{ padding: "6px 0", fontSize: 14, fontWeight: 600, color: "#1a1a2e", borderBottom: "1px solid #eee" }}>{winner.guest_name}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Email</td>
                          <td style={{ padding: "6px 0", fontSize: 12, color: "#1a1a2e", borderBottom: "1px solid #eee" }}>{winner.guest_email}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Prize</td>
                          <td style={{ padding: "6px 0", fontSize: 14, fontWeight: 600, color: "#c9b037", borderBottom: "1px solid #eee" }}>{winner.prize}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>From</td>
                          <td style={{ padding: "6px 0", fontSize: 12, color: "#1a1a2e", borderBottom: "1px solid #eee" }}>{h}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: "6px 0", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Date</td>
                          <td style={{ padding: "6px 0", fontSize: 12, color: "#1a1a2e", borderBottom: "1px solid #eee" }}>{new Date(winner.draw_date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div style={{ width: 120, textAlign: "center", borderLeft: "1px dashed #ddd", paddingLeft: 20, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <QRCodeSVG value={qr} size={90} level="M" fgColor="#c9b037" />
                    <p style={{ fontSize: 8, color: "#888", marginTop: 6 }}>Scan to verify</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ background: "#faf9f5", borderTop: "1px solid #eee", padding: "10px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p style={{ fontSize: 8, color: "#999", lineHeight: 1.5, maxWidth: 420 }}>
                  Present this voucher at the front desk to claim your prize. Non-transferable. Cannot be combined with other offers.
                </p>
                <p style={{ fontSize: 8, color: "#999" }}>{ph} | {em}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", marginTop: 16 }}>
        <Button onClick={() => printContent(ref, `Gift Voucher - ${winner.guest_name}`)} className="bg-[#c9b037] text-white hover:bg-[#b09a2e] px-8 py-3 text-xs uppercase tracking-widest" data-testid={`print-gift-${winner.id}`}>
          <Printer size={14} className="mr-2" /> Print Voucher
        </Button>
      </div>
    </div>
  );
};

/* ============================================================
   COMPACT CARD for admin trophy list
   ============================================================ */
export const TrophyCard = ({ winner, formatDateShort, onDelete, DeleteButton }) => (
  <div className="card-flamingo overflow-hidden" data-testid={`trophy-row-${winner.id}`}>
    <div className="h-1.5 bg-gradient-to-r from-[#1a1a2e] to-[#c9b037]" />
    <div className="p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#1a1a2e]/10 rounded-full flex items-center justify-center border border-[#1a1a2e]/20">
            <Award className="text-[#c9b037]" size={18} />
          </div>
          <div>
            <p className="font-heading text-base font-semibold text-[#0A192F]">{winner.guest_name}</p>
            <p className="text-xs text-[#4A5568]">{winner.guest_email}</p>
          </div>
        </div>
        {DeleteButton && <DeleteButton onDelete={() => onDelete(winner.id)} label="trophy" testId={`delete-trophy-${winner.id}`} />}
      </div>
      <div className="bg-[#F7F5F0] p-3 space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-xs tracking-wider uppercase font-bold text-[#0A192F]/40">Prize</span>
          <span className="font-heading text-[#c9b037] font-semibold">{winner.prize}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-xs tracking-wider uppercase font-bold text-[#0A192F]/40">Cert #</span>
          <span className="font-mono text-xs text-[#0A192F]">{winner.barcode || "—"}</span>
        </div>
        {(winner.check_in_date || winner.check_out_date) && (
          <div className="flex justify-between">
            <span className="text-xs tracking-wider uppercase font-bold text-[#0A192F]/40">Dates</span>
            <span className="text-[#0A192F]">{formatDateShort(winner.check_in_date)} — {formatDateShort(winner.check_out_date)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-xs tracking-wider uppercase font-bold text-[#0A192F]/40">Status</span>
          <span className={`px-2 py-0.5 text-xs font-bold rounded ${winner.notified ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
            {winner.notified ? "Notified" : "Pending"}
          </span>
        </div>
        {winner.issued_by && (
          <div className="flex justify-between">
            <span className="text-xs tracking-wider uppercase font-bold text-[#0A192F]/40">Issued By</span>
            <span className="text-xs font-medium text-[#4A5568]" data-testid={`trophy-issued-by-${winner.id}`}>{winner.issued_by}</span>
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <QRCodeSVG value={winner.barcode || "N/A"} size={48} level="L" fgColor="#1a1a2e" />
        <span className="text-xs text-[#4A5568] font-mono">{winner.barcode}</span>
      </div>
    </div>
  </div>
);
